import React from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button, { LinkButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';

import type { HierarchyData } from '../../../types';

import { MiniHierarchyList } from './MiniHierarchyList';

const styles = cssMap({
	focusReset: {
		outline: 'none',
	},
	fill: {
		flexGrow: 1,
		flexShrink: 1,
		minHeight: 0,
		height: '100%',
	},
	listRegion: {
		flexGrow: 1,
		flexShrink: 1,
		minHeight: 0,
		display: 'flex',
		flexDirection: 'column',
	},
});

const messages = defineMessages({
	emptyState: {
		id: 'user-profile-card.ReportingLinePane.emptyState',
		defaultMessage: 'No reporting line information is available.',
		description:
			'Message shown on the reporting line tab of the user profile card when the user has no reporting-line data',
	},
	viewFullReportingLine: {
		id: 'user-profile-card.ReportingLinePane.viewFullReportingLine',
		defaultMessage: 'View full reporting line',
		description:
			'Label on the link that opens the full reporting line on the user profile page from the profile card',
	},
	heading: {
		id: 'user-profile-card.ReportingLinePane.heading',
		defaultMessage: 'Reporting line',
		description: 'Heading of the reporting-line view on the user profile card',
	},
	back: {
		id: 'user-profile-card.ReportingLinePane.back',
		defaultMessage: 'Back to profile',
		description:
			'Accessible label for the button that returns from the reporting-line view to the main profile view on the user profile card',
	},
	backShort: {
		id: 'user-profile-card.ReportingLinePane.backShort',
		defaultMessage: 'Back',
		description:
			'Visible label on the button that returns from the reporting-line view to the main profile view on the user profile card',
	},
});

interface ReportingLinePaneProps {
	/** Reporting-line hierarchy fetched alongside the card. Undefined when unavailable. */
	hierarchyData?: HierarchyData;
	/** Profile-page URL used for the "View full reporting line" link. */
	viewFullUrl?: string;
	target?: string;
	onNavigate?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
	/** Fired when the "View full reporting line" link is clicked. */
	onViewFullClick?: () => void;
	/** When provided, renders a back affordance in the header that returns to the profile view. */
	onBack?: () => void;
	/** Focus target moved to when this view is revealed (the heading), for keyboard/SR users. */
	headingRef?: React.RefObject<HTMLDivElement>;
	/**
	 * Bumps whenever the reporting-line view is (re)opened, so the list re-centers on the profiled
	 * user each time the pane is shown.
	 */
	resetToken?: number;
	testId?: string;
}

/**
 * Content of the card's "Reporting line" view: an optional back header, a single scrollable
 * hierarchy column, and a link to the full reporting line on the profile page. Falls back to an
 * empty-state message when no hierarchy data is available.
 */
export function ReportingLinePane({
	hierarchyData,
	viewFullUrl,
	target,
	onNavigate,
	onViewFullClick,
	onBack,
	headingRef,
	resetToken,
	testId,
}: ReportingLinePaneProps): React.JSX.Element {
	const intl = useIntl();

	return (
		<Stack space="space.200" testId={testId} xcss={styles.fill}>
			{onBack && (
				<Inline space="space.100" alignBlock="center">
					<Box ref={headingRef} tabIndex={-1} xcss={styles.focusReset}>
						<Button
							iconBefore={ArrowLeftIcon}
							appearance="default"
							onClick={onBack}
							testId="hierarchy-back-button"
							aria-label={intl.formatMessage(messages.back)}
						>
							{intl.formatMessage(messages.backShort)}
						</Button>
					</Box>
				</Inline>
			)}
			{hierarchyData ? (
				<Box xcss={styles.listRegion}>
					<MiniHierarchyList
						data={hierarchyData}
						scrollToken={resetToken}
						moreHref={viewFullUrl}
						moreTarget={target}
						onMoreClick={onViewFullClick}
						onMoreNavigate={onNavigate}
						testId="hierarchy-list"
					/>
				</Box>
			) : (
				<Box paddingBlock="space.100">
					<Text size="small" color="color.text.subtlest">
						{intl.formatMessage(messages.emptyState)}
					</Text>
				</Box>
			)}
			{viewFullUrl && (
				<Inline alignInline="center">
					<LinkButton
						href={viewFullUrl}
						target={target}
						rel={target === '_blank' ? 'noopener noreferrer' : undefined}
						onClick={(event) => {
							onViewFullClick?.();
							onNavigate?.(event);
						}}
						appearance="default"
						testId="hierarchy-view-full-link"
					>
						{intl.formatMessage(messages.viewFullReportingLine)}
					</LinkButton>
				</Inline>
			)}
		</Stack>
	);
}
